import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import type { AdRequestPopupDetails } from "@/UI/popups/AdRequestPopup";
import AdRequestPopup from "@/UI/popups/AdRequestPopup";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { useEffect, useRef } from "react";
import "./styles.css";

type RequestStatus = "sending" | "reviewing" | "accepted" | "rejected";

interface AdRequest {
    _id: string;
    message: string;
    whatsapp: string;
    type: "product" | "store" | "menu" | "external" | null;
    status: RequestStatus;
    intendedStartDate: string | null;
    intendedEndDate: string | null;
    response: string | null;
    banner: any | null;
    createdAt: string;
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

export default function AdBannersScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const a = t.adBanners;
    const dir = lang === "ar" ? "rtl" : "ltr";
    const { addComp, removeComp } = usePopupStore();

    // ── Fetch current request ─────────────────────────────────────────────────
    const { data, loading, status, invoke } = useFetch("ad-requests/current", { method: "GET" });

    const refresh = () => invoke({ method: "GET" });

    const adRequest: AdRequest | null = data?.data?.adRequest ?? null;
    const hasRequest = status === 200 && adRequest !== null;
    // const noRequest = status === 404;

    // ── Delete request ────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("ad-requests", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            if (deleteStatus === 200) refresh();
        }
    }, [deleting]);

    const openCancelConfirm = () => {
        const name = "cancel-ad-request";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: a.cancelConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => deleteInvoke({ param: adRequest!._id }),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Create request popup ──────────────────────────────────────────────────
    const openRequestForm = () => {
        const name = "ad-request-form";
        removeComp(name);
        addComp({
            name,
            Component: AdRequestPopup,
            details: { onSuccess: refresh } as AdRequestPopupDetails,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div className="abs-screen" dir={dir}>
                <div className="abs-loading">
                    <span className="abs-spinner" />
                </div>
            </div>
        );
    }

    if (hasRequest && adRequest) {
        const statusKey = adRequest.status as RequestStatus;
        return (
            <div className="abs-screen" dir={dir}>
                <div className="abs-request-card">
                    {/* Card header */}
                    <div className="abs-card-header">
                        <h2 className="abs-card-title">{a.currentTitle}</h2>
                        <span className={`abs-status-badge abs-status-badge--${statusKey}`}>{a.status[statusKey]}</span>
                    </div>

                    {/* Details grid */}
                    <div className="abs-details">
                        <div className="abs-detail-row">
                            <span className="abs-detail-label">{a.messageLabel}</span>
                            <span className="abs-detail-value abs-detail-value--message">{adRequest.message}</span>
                        </div>
                        <div className="abs-detail-row">
                            <span className="abs-detail-label">{a.whatsappLabel}</span>
                            <span className="abs-detail-value abs-detail-value--mono">{adRequest.whatsapp}</span>
                        </div>
                        {adRequest.type && (
                            <div className="abs-detail-row">
                                <span className="abs-detail-label">{a.typeLabel}</span>
                                <span className="abs-detail-value">{a.types[adRequest.type]}</span>
                            </div>
                        )}
                        {adRequest.intendedStartDate && (
                            <div className="abs-detail-row">
                                <span className="abs-detail-label">{a.startDateLabel}</span>
                                <span className="abs-detail-value">{formatDate(adRequest.intendedStartDate)}</span>
                            </div>
                        )}
                        {adRequest.intendedEndDate && (
                            <div className="abs-detail-row">
                                <span className="abs-detail-label">{a.endDateLabel}</span>
                                <span className="abs-detail-value">{formatDate(adRequest.intendedEndDate)}</span>
                            </div>
                        )}
                        {adRequest.response && (
                            <div className="abs-detail-row">
                                <span className="abs-detail-label">{a.responseLabel}</span>
                                <span className={`abs-detail-value abs-detail-value--response abs-detail-value--response-${statusKey}`}>
                                    {adRequest.response}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Banner preview if accepted */}
                    {adRequest.status === "accepted" && adRequest.banner && (
                        <div className="abs-banner-section">
                            <span className="abs-detail-label">{a.bannerLabel}</span>
                            <div className="abs-banner-preview">
                                {adRequest.banner.image?.[0]?.value && (
                                    <img src={adRequest.banner.image[0].value} alt="banner" className="abs-banner-img" />
                                )}
                            </div>
                        </div>
                    )}

                    {/* Cancel button — only if still sending */}
                    {adRequest.status === "sending" && (
                        <div className="abs-card-footer">
                            <button type="button" className="abs-cancel-btn" onClick={openCancelConfirm} disabled={deleting}>
                                {deleting ? <span className="abs-spinner abs-spinner--dark" /> : a.cancelBtn}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Overview (no request, or any other state)
    return (
        <div className="abs-screen" dir={dir}>
            <div className="abs-overview">
                <div className="abs-overview-icon">
                    <img src="/icons/inputs-group-icon.svg" alt="" width={36} height={36} />
                </div>
                <h1 className="abs-overview-title">{a.overviewTitle}</h1>
                <p className="abs-overview-desc">{a.overviewDesc}</p>

                <div className="abs-steps">
                    {(
                        [
                            { label: a.step1Label, desc: a.step1Desc, num: 1 },
                            { label: a.step2Label, desc: a.step2Desc, num: 2 },
                            { label: a.step3Label, desc: a.step3Desc, num: 3 },
                        ] as const
                    ).map((step) => (
                        <div key={step.num} className="abs-step">
                            <div className="abs-step-num">{step.num}</div>
                            <div className="abs-step-body">
                                <span className="abs-step-label">{step.label}</span>
                                <span className="abs-step-desc">{step.desc}</span>
                            </div>
                        </div>
                    ))}
                </div>

                <button type="button" className="abs-request-btn" onClick={openRequestForm}>
                    {a.requestBtn}
                </button>
            </div>
        </div>
    );
}
